/* eslint-disable @typescript-eslint/no-dynamic-delete */
import cloneDeep from 'lodash-es/cloneDeep';
import {
  TProperties,
  TPropsSuscriptor,
  TUpdateFieldConfiguration,
} from './types';
import { debugDispatcher } from '../debug';
import { TId } from '../types';
import { persistentStorage } from '../storage';

const propsLog = 'propsLog';
export const PropsSelectorUndefinedObject = {};

interface IPropsStoreConf {
  logCommands: {
    propsStore?: string;
    updateProp?: string;
    propsSuscriptors?: string;
    propsLog?: string;
  };
}

export class PropsStore<
  PropsType extends Record<TId, unknown> = Record<TId, unknown>,
> {
  log = persistentStorage[propsLog];

  fields: Record<TId, PropsType> = {};

  suscriptors: Record<TId, TPropsSuscriptor<PropsType>[]> = {};

  loggers: Record<string, (props: unknown[]) => void> = {
    propsLog: ([log]) => {
      this.log = log;
      persistentStorage[propsLog] = log;
    },
    propsStore: ([fieldId]) => {
      if (fieldId) console.info(this.fields[fieldId as TId]);
      else console.info(this.fields);
    },
    propsSuscriptors: ([fieldId]) => {
      if (fieldId) console.info(this.suscriptors[fieldId as TId]);
      else console.info(this.suscriptors);
    },
    updateProp: ([fieldId, newProps]) => {
      this.updateField(fieldId as TId, newProps as PropsType);
    },
  };

  constructor(private configuration?: IPropsStoreConf) {
    if (this.configuration) {
      if (this.configuration.logCommands.propsStore)
        debugDispatcher.on(
          this.configuration.logCommands.propsStore,
          this.loggers.propsStore,
          'Muestra el contenido actual del propsStore',
        );
      if (this.configuration.logCommands.updateProp)
        debugDispatcher.on(
          this.configuration.logCommands.updateProp,
          this.loggers.updateProp,
          'dd.updateProp(fieldId, newProps). El objeto newProps será mergeado sobre las props actuales',
        );
      if (this.configuration.logCommands.propsSuscriptors)
        debugDispatcher.on(
          this.configuration.logCommands.propsSuscriptors,
          this.loggers.propsSuscriptors,
          'Muestra los suscriptores actuales del propsStore',
        );
      if (this.configuration.logCommands.propsLog)
        debugDispatcher.on(
          this.configuration.logCommands.propsLog,
          this.loggers.propsLog,
          'Si se pasa como true, hace log de las acciones. Si se pasa como trace(fieldId), se hace log de todas las acciones y trace del campo especificado. Si se pasa trace(), hace log y trace de todas las acciones.',
        );
    }
  }

  destructor() {
    if (this.configuration) {
      if (this.configuration.logCommands.propsStore)
        debugDispatcher.off(
          this.configuration.logCommands.propsStore,
          this.loggers.propsStore,
        );
      if (this.configuration.logCommands.updateProp)
        debugDispatcher.off(
          this.configuration.logCommands.updateProp,
          this.loggers.updateProp,
        );
      if (this.configuration.logCommands.propsSuscriptors)
        debugDispatcher.off(
          this.configuration.logCommands.propsSuscriptors,
          this.loggers.propsSuscriptors,
        );
      if (this.configuration.logCommands.propsLog)
        debugDispatcher.off(
          this.configuration.logCommands.propsLog,
          this.loggers.propsLog,
        );
    }
  }

  getAllFields() {
    return cloneDeep(this.fields);
  }

  /**
   * Devuelve los props actuales de un campo.
   */
  getFieldProps<ParsedPropsType = PropsType>(fieldId: TId) {
    return this.fields[fieldId] as unknown as ParsedPropsType;
  }

  removeField(fieldId: TId) {
    this.suscriptors[fieldId] = [];
    delete this.fields[fieldId];
    if (this.log) console.info(`propsStore: removeNode ${fieldId}`);
  }

  /**
   * Permite establecer un suscriptor que será llamado
   * cada vez que las props del campo especificado cambien.
   */
  suscribe(fieldId: TId, callback: TPropsSuscriptor<PropsType>) {
    if (!this.suscriptors[fieldId]) this.suscriptors[fieldId] = [];

    this.suscriptors[fieldId].push(callback);

    callback(this.fields[fieldId] ?? PropsSelectorUndefinedObject);

    return () => {
      this.suscriptors[fieldId] = this.suscriptors[fieldId].filter(
        (current) => {
          return current !== callback;
        },
      );
    };
  }

  /**
   * Actualiza o crea las props de un campo.
   *
   * La tercera prop está relacionada
   */
  updateField<NewPropsType extends Record<TId, unknown> = Partial<PropsType>>(
    fieldId: TId,
    props: Partial<NewPropsType>,
    conf?: TUpdateFieldConfiguration,
  ) {
    if (fieldId === undefined) return;

    const { noEmit, isUrgent } = conf ?? {};
    const newProps = { ...this.fields[fieldId], ...props };
    this.fields[fieldId] = newProps;

    if (this.log === true || this.log === fieldId)
      console.info({ fieldId, props });
    if (this.log === 'trace') console.trace();
    else {
      const logId =
        typeof this.log === 'string' &&
        (this.log.match(/trace\(([^)]+)\)/) ?? [])[1];
      if (fieldId === logId) console.trace();
    }

    if (!noEmit) {
      this.suscriptors[fieldId]?.forEach((current) => {
        return current(newProps, isUrgent);
      });
      this.suscriptors.any?.forEach((current) => {
        return current({ ...newProps, fieldId }, isUrgent);
      });
    }
  }
}

export const propsStore = new PropsStore<TProperties>({
  logCommands: {
    propsLog: 'propsLog',
    propsStore: 'propsStore',
    propsSuscriptors: 'propsSuscriptors',
    updateProp: 'updateProp',
  },
});
